/**
 * Pledge Notifications (Sprint 171 D-006; re-scoped 2026-09-27)
 *
 * Pledge notification routing -- reviewer assignment, SLA thresholds,
 * notification channels and the daily digest -- is configured per tenant. On
 * platform.aaim.com the operator is not inside any tenant, so this page names
 * the tenant first and opens that tenant's own editors.
 *
 * It used to be an editor for `Institution.configuration.notificationConfig`
 * behind GET/PUT /api/admin/tenant-config/notifications, which resolves the
 * tenant from the caller's JWT tenantId claim or an x-tenant-id header. The
 * page named no tenant and had no tenant selector: a platform-admin SSO token
 * carries no tenantId and got a 400, while a fixture token that carries one
 * edited that fixture tenant's blob under the words "for this tenant". The
 * per-tenant editors already exist as the Tenant Detail Notifications, SLA and
 * Reviewers tabs, backed by the explicit-tenant routes
 * /tenants/{tenantId}/notifications, /sla and /reviewers.
 */

import { AlertCircle } from 'lucide-react';
import { BodyText, Card, Skeleton, Table } from '@aaimplatform/ui';
import type { TableColumn } from '@aaimplatform/ui';
import type { Tenant } from '../../api/client';
import { useTenants } from '../../hooks/useTenants';
import { getTypeLabel } from '../../utils/institution-labels';
import { TENANT_PURPOSE_LABEL, enumLabel } from '../../utils/status-labels';
import { RouterTableLink } from '../../components/RouterTableLink';

/** The Tenant Detail tabs that hold a tenant's pledge notification settings. */
export const TENANT_ROUTING_TABS = [
  { tab: 'notifications', label: 'Channels and digest' },
  { tab: 'sla', label: 'SLA thresholds' },
  { tab: 'reviewers', label: 'Reviewer assignment' },
] as const;

const COLUMNS: TableColumn<Tenant>[] = [
  {
    key: 'name',
    label: 'Tenant',
    render: (_value, row) => (
      <div>
        <div className="text-sm font-sans font-medium text-primary leading-normal">
          {row.name}
        </div>
        {/* The purpose rides beside the type, so a Demo or Probe tenant is
            told apart before its settings are opened (operator ruling
            2026-10-22). */}
        <div className="text-xs font-sans text-tertiary leading-normal">
          {`${getTypeLabel(row.type)} | ${enumLabel(TENANT_PURPOSE_LABEL, row.purpose)}`}
        </div>
      </div>
    ),
  },
  {
    key: 'id',
    label: 'Settings',
    render: (_value, row) => (
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {/* The kit TableLink in the secondary color: a navigation link, not a
            CTA, so it carries no red. The tenant name rides in screen-reader
            text so each of the repeated labels names its tenant. */}
        {TENANT_ROUTING_TABS.map(({ tab, label }) => (
          <RouterTableLink key={tab} to={`/tenants/${row.id}?tab=${tab}`} className="text-sm">
            {label}
            <span className="sr-only"> for {row.name}</span>
          </RouterTableLink>
        ))}
      </div>
    ),
  },
];

export function NotificationRoutingPage() {
  const { data, isLoading, error } = useTenants();
  const tenants = data?.data ?? [];

  return (
    <div>
      {/* The layout's header bar carries the page title; this is its
          description, not a second heading. */}
      <BodyText variant="ui" className="text-sm text-tertiary mb-6 max-w-3xl">
        Pledge notification routing is set per tenant: reviewer assignment,
        SLA thresholds, notification channels and the daily digest. Choose the
        tenant, then open the settings to change. Each change applies to that
        tenant only.
      </BodyText>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load tenants: {error.message}
          </div>
        </div>
      )}

      {isLoading ? (
        <Card className="p-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </Card>
      ) : !error && tenants.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm font-sans text-secondary">No tenants are onboarded.</p>
        </Card>
      ) : !error ? (
        <Card className="p-0">
          <Table<Tenant> data={tenants} columns={COLUMNS} hoverable label="Notification routing by tenant" />
        </Card>
      ) : null}
    </div>
  );
}
